import formatFloat from 'api/getChartSeries/formatFloat';
import { isSecondaryDimensionAInEffect } from 'components/V5GadgetForm/isSecondaryDimensionAEligible';
import ensureNDecimalPlaces from 'hooks/useValueFormatters/ensureNDecimalPlaces';
import numberToCommaString from 'hooks/useValueFormatters/numberToCommaString';

const DEFAULT_PRECISION = 2;

export const getRankField = (metricId: string) => `${metricId}-rank`;
export const getPercentageOfTotalField = (metricId: string) =>
  `${metricId}-percentage`;

// Rank and % of total columns need a row per term of a single text or date
// dimension, with a column per metric (so not reversed), and not rows of
// (A, secondary A) pairs
export const canShowRankAndPercentage = (chartDef: V5ChartDefinition) => {
  const { dimensionA } = chartDef;
  return (
    chartDef.gadgetType === 'matrix' &&
    !!dimensionA &&
    !chartDef.dimensionB &&
    !chartDef.reverseAxes &&
    !chartDef.trendByHourInterval &&
    !isSecondaryDimensionAInEffect(chartDef) &&
    !dimensionA.rangeInput &&
    !dimensionA.isGroupByDayOfWeek &&
    !dimensionA.isGroupByMonth
  );
};

export const getRankedSeries = (chartDef: V5ChartDefinition) =>
  canShowRankAndPercentage(chartDef)
    ? chartDef.series.filter(
        (s) => s.isRankingEnabled || s.isPercentageOfTotalEnabled,
      )
    : [];

const toNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

// Standard competition ranking (1, 2, 2, 4): tied values share the better
// rank. Missing values aren't ranked.
export const rankValues = ({
  values,
  isHigherBetter,
}: {
  values: (number | undefined)[];
  isHigherBetter: boolean;
}): (number | undefined)[] => {
  const sorted = values
    .filter((v): v is number => v !== undefined)
    .sort((a, b) => (isHigherBetter ? b - a : a - b));
  const rankByValue = new Map<number, number>();
  sorted.forEach((v, index) => {
    if (!rankByValue.has(v)) {
      rankByValue.set(v, index + 1);
    }
  });
  return values.map((v) => (v === undefined ? undefined : rankByValue.get(v)));
};

// Whether a share of the total means anything for the metric: only for
// normal metrics that add up (a count or sum, not an average or a max) and
// compound metrics, as the Ranking Matrix's server-side % of total does
// (groupByAggregation applyPercentage)
export const hasPercentageOfTotal = (metric: Metrics.Metric | undefined) =>
  !metric ||
  metric.type !== 'normal' ||
  metric.aggFunc === 'count' ||
  metric.aggFunc === 'sum';

export const formatPercentage = ({
  value,
  precision,
}: {
  value: number;
  precision: number;
}) =>
  `${ensureNDecimalPlaces({
    value: numberToCommaString({ value: formatFloat(value, precision) }),
    precision,
  })}%`;

// Adds each ranked series' rank and share of the total to the matrix rows,
// as formatted strings like the matrix's other cells (so they render and
// sort the same way). Computed from the rows fetched: with a Top N limit
// that's the rank and share among the rows shown. Rows are matched to the
// response by term (or raw date), so this works for both the plain and the
// comparison rows.
const addRankAndPercentageOfTotal = <T extends { [key: string]: unknown }>({
  rows,
  chartDef,
  response,
  totals,
  groupByField,
  usedMetrics,
}: {
  rows: T[];
  chartDef: V5ChartDefinition;
  // The current period's grouped rows and totals, unformatted
  response: { [key: string]: unknown }[];
  totals: { [key: string]: unknown }[];
  groupByField: string;
  usedMetrics: Metrics.Metric[];
}): T[] => {
  const rankedSeries = getRankedSeries(chartDef);
  if (rankedSeries.length === 0) {
    return rows;
  }

  const cellsByTerm: Record<string, Record<string, string>> = {};
  const terms = response.map((r) => String(r[groupByField]));
  terms.forEach((term) => {
    cellsByTerm[term] = {};
  });
  const fields: string[] = [];

  rankedSeries.forEach((s) => {
    const metric = usedMetrics.find((m) => m.id === s.metricId);
    const values = response.map((r) => toNumber(r[s.metricId]));

    if (s.isRankingEnabled) {
      const field = getRankField(s.metricId);
      fields.push(field);
      const ranks = rankValues({
        values,
        isHigherBetter: metric?.formatting.positiveDeltaIsGood !== false,
      });
      terms.forEach((term, index) => {
        const rank = ranks[index];
        cellsByTerm[term][field] = rank === undefined ? '-' : `${rank}`;
      });
    }

    if (s.isPercentageOfTotalEnabled) {
      const field = getPercentageOfTotalField(s.metricId);
      fields.push(field);
      const total =
        s.isTotalEnabled === false ||
        totals.length === 0 ||
        !hasPercentageOfTotal(metric)
          ? undefined
          : toNumber(totals[0][s.metricId]);
      const precision =
        s.percentageOfTotalPrecision ??
        metric?.formatting.precision ??
        DEFAULT_PRECISION;
      terms.forEach((term, index) => {
        const value = values[index];
        cellsByTerm[term][field] =
          value === undefined || !total
            ? '-'
            : formatPercentage({ value: (value / total) * 100, precision });
      });
    }
  });

  // Rows with no current value (only in the comparison period) show '-'
  const missing = fields.reduce<Record<string, string>>(
    (acc, field) => ({ ...acc, [field]: '-' }),
    {},
  );
  return rows.map((row) => {
    const key = row['raw-date'] ?? row[groupByField];
    const cells = key === undefined ? undefined : cellsByTerm[String(key)];
    return { ...row, ...missing, ...cells };
  });
};

export default addRankAndPercentageOfTotal;
